import type React from "react";
import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetPatients } from "api/hooks/useGetPatients";
import type { Patients } from "api/schemas/Patients";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { VideoCallAwareContainer } from "shared/atoms/VideoCallAwareContainer/VideoCallAwareContainer";
import { View } from "shared/containers/View/View";
import { PatientsFilterContext } from "utils/contexts";

import { PatientsFilter } from "../PatientsList";
import type { Filters } from "../PatientsList/PatientsFilter/useFilters";
import { PatientsTable } from "../PatientsList/PatientsTable/PatientsTable";

export const PatientsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { matchesActiveFilters } = useContext<Filters>(PatientsFilterContext);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [patients, setPatients] = useState<Patients[]>([]);

  const { profile } = useProfileContext();
  invariant(profile);

  const { data = [], isLoading } = useGetPatients({
    userID: profile.id,
    config: {
      params: {
        enabled_columns: ["primaryJoint", "lastActivityCompletedAt", "activityAdherence", "addressState"],
      },
    },
  });

  useEffect(() => {
    if (data.length > 0) {
      let filtered = data.filter(matchesActiveFilters);

      if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(patient => {
          const searchableText = [
            patient.id,
            patient.first_name,
            patient.last_name,
            `${patient.first_name} ${patient.last_name}`,
            patient.email,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchableText.includes(lowerSearch);
        });
      }

      setPatients(filtered);
    }
  }, [data, matchesActiveFilters, searchTerm]);

  return (
    <View
      title={t("views.patients")}
      actionButtons={[
        <BaseButton
          text={t("invite.submit")}
          icon="user-plus"
          style={{ flexGrow: 0 }}
          onClick={() => navigate("/invitations")}
          uppercase={false}
          size="large"
        />,
      ]}
    >
      <PageWrapper>
        <ListContainer>
          <TableWrapper>
            <PatientsTable
              isLoading={isLoading}
              patients={patients}
              totalPatients={data.length}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onFilterClick={() => setIsOpen(true)}
            />
          </TableWrapper>
        </ListContainer>

        <PatientsFilter open={isOpen} close={() => setIsOpen(false)} patients={data} />
      </PageWrapper>
    </View>
  );
};

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  box-sizing: border-box;
  position: relative;
  contain: inline-size;
  && {
    flex: 1;
  }
`;

const ListContainer = styled(VideoCallAwareContainer)`
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;

  ${({ theme }) => theme.belowMobileBreakpoint} {
    padding: 0;
    min-height: auto;
    overflow: visible;
  }
`;

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;

  ${({ theme }) => theme.belowMobileBreakpoint} {
    min-height: 400px;
    overflow: visible;
  }
`;
